import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Link,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import { EmptyState, ErrorState, Loading } from "@lopie/kernel-ui";
import { api } from "../lib/projects-api.ts";

const PRIORITY_COLOR = { high: "error", med: "warning", low: "default" };

/**
 * Every task assigned to you, across projects.
 *
 * "You" is resolved server-side to the kernel Person whose emails include the
 * signed-in address, because an assignee is a Person id and not a Cognito sub
 * — so this list is empty, not wrong, for somebody who is not in the employee
 * directory, and the screen says which. Grouped by project, because the first
 * question is *whose board is this on* and the second is *what is it*.
 */
export default function MyTasks({ onOpenProject }) {
  const [tasks, setTasks] = useState(null);
  const [projects, setProjects] = useState([]);
  const [meMissing, setMeMissing] = useState(false);
  const [failure, setFailure] = useState(null);

  const load = () => {
    setFailure(null);
    api.myTasks()
      .then((r) => { setTasks(r.items); setMeMissing(!r.me); })
      .catch(setFailure);
    api.projects().then((r) => setProjects(r.items)).catch(() => setProjects([]));
  };
  useEffect(load, []);

  if (failure) return <ErrorState failure={failure} onRetry={load} sx={{ mt: 2 }} />;
  if (tasks == null) return <Loading label="Finding your tasks" />;

  const projectById = new Map(projects.map((p) => [p.id, p]));
  const groups = new Map();
  for (const t of tasks) {
    if (!groups.has(t.projectId)) groups.set(t.projectId, []);
    groups.get(t.projectId).push(t);
  }

  return (
    <Stack spacing={2} sx={{ pt: 2, maxWidth: 1100 }}>
      <Typography variant="h5">My tasks</Typography>

      {meMissing ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            You are not in the employee directory this kernel reads, so no task
            is assigned to you by a Person id. This list fills in once you are a
            Person tagged <code>employee</code> that the assignee picker offers.
          </Typography>
        </Paper>
      ) : null}

      {!tasks.length && !meMissing ? (
        <EmptyState title="Nothing assigned to you"
          description="Tasks you are the assignee of show up here, across every project." />
      ) : null}

      {[...groups.entries()].map(([projectId, group]) => {
        const project = projectById.get(projectId);
        return (
          <Paper key={projectId} variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "3px",
                bgcolor: project?.color || "primary.main" }} />
              <Typography variant="subtitle1" sx={{ flex: 1 }}>
                {project?.name || "A project"}
              </Typography>
              {project ? (
                <Link component="button" onClick={() => onOpenProject(project)} underline="hover">
                  Open board
                </Link>
              ) : null}
            </Stack>
            <Stack divider={<Box sx={{ borderTop: 1, borderColor: "divider" }} />}>
              {group
                .sort((a, b) => (a.dueDate || "~").localeCompare(b.dueDate || "~"))
                .map((t) => (
                  <Stack key={t.id} direction="row" spacing={1}
                    sx={{ alignItems: "center", py: 0.75, flexWrap: "wrap" }}>
                    <Typography variant="caption" color="text.secondary"
                      sx={{ fontFamily: "monospace", minWidth: 70 }}>
                      {project?.key}-{t.number}
                    </Typography>
                    <Typography variant="body2" sx={{ flex: 1, minWidth: 160 }}>{t.title}</Typography>
                    {t.tshirtSize ? <Chip size="small" variant="outlined" label={t.tshirtSize} /> : null}
                    {t.priority ? (
                      <Chip size="small" variant="outlined"
                        color={PRIORITY_COLOR[t.priority] || "default"} label={t.priority} />
                    ) : null}
                    {t.dueDate ? (
                      <Typography variant="caption" color="text.secondary">due {t.dueDate}</Typography>
                    ) : null}
                  </Stack>
                ))}
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}
